import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import CustomDataGridV2 from "../../../../components/CustomDataGridV2";
import { FilterSelect } from "../../../../components/FilterSelect";
import Flex from "../../../../components/Flex";
import PageLayout from "../../../../components/PageLayout";
import Text from "../../../../components/Text";
import CustomTitle from "../../../../components/app/CustomTitle";
import UploadData, {
  EntityType,
} from "../../../../components/app/admin/Library/UploadData";
import ViewRecordDrawer from "../../../../components/app/admin/Library/ViewRecordDrawer";
import { useRoleController } from "../../../../components/useRoleController";
import { DrugCatalogRowsType } from "../../../../dummy-data/drug-catalog";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import useDataImportList from "../../../../queries/use-data-import-list";
import {
  ExportPatientListTypeEnum,
  GetDataImportRecordsTypeEnum,
  MigrationControllerApi,
} from "../../../../services";
import { formatDateTime } from "../../../../utils/date-time-utility";
import {
  DEFAULT_PAGE_SIZE,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getConfigObject,
} from "../../../../utils/utils";
import StatusChip from "../../../../components/StatusChip";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

const STATUS_OPTIONS = [
  { value: "true", label: "All" },
  { value: "pass", label: "Pass" },
  // { value: "processing", label: "Processing" },
  { value: "fail", label: "Fail" },
];

export const ENTITY_TYPE_MAPPER: Record<GetDataImportRecordsTypeEnum, string> =
  {
    PROVIDER: "Provider Data",
    CPT: "CPT Catalog",
    // "Drug Catalog": "Drug Catalog",
    ICD: "ICD 10 Catalog",
    LOINC: "LOINC Catalog",
    HCPCS: "HCPCS Catalog",
    PATIENT: "Patient Data",
    LAB_TEST: "Lab Test",
    IMAGING_STUDY: "Imaging Study",
    PAYER_CATALOG: "Payer Catalog",
    DRUG_CATALOG: "Drug Catalog",
  };

const DataImport = () => {
  const { hasUpdate: hasUpdateBulkUpload } = useRoleController("Bulk Upload");
  const [UploadDataModalDisclosure, ViewRecordModalDisclosure] =
    useReduceDisclosure(2);

  async function downloadFailedRecords(dataImportUuid: string) {
    try {
      const api = new MigrationControllerApi(getConfigObject());
      const resp: any = await api.getDataImportById(dataImportUuid, undefined);

      const fileUrl = resp?.data?.data?.fileUrl;
      if (!fileUrl) {
        return;
      }

      // Fetch the file from the URL
      const response = await fetch(fileUrl);
      if (!response.ok) {
        return;
      }

      const blob = await response.blob();

      // Create a download link with the custom file name
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "FailedRecords.txt";
      link.click();

      // Clean up
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error downloading failed records:", error);
    }
  }

  const [curDataImport, setCurDataImport] = useState<{
    type: ExportPatientListTypeEnum;
    xTENANTID: string;
    uuid: string;
    providerUuid?: string;
  }>({
    type: "PATIENT",
    xTENANTID: "",
    uuid: "",
    providerUuid: "",
  });

  const columns = applyDefaultColumnSettings<DrugCatalogRowsType>(
    [
      {
        field: "importInitiated",
        headerName: "Import Initiated",
        flex: 0.7,
        minWidth: 220,
        renderCell: ({ row }) =>
          row?.createdDateTime ? formatDateTime(row.createdDateTime) : "-",
      },
      {
        field: "type",
        headerName: "Entity",
        flex: 0.8,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Text
            isTabletext
            c={"primary"}
            sx={{ cursor: "pointer" }}
            onClick={() => {
              const providerUuid = Object.keys(row?.providerGroup ?? {});

              setCurDataImport({
                uuid: row?.uuid,
                xTENANTID: row?.schemaName,
                providerUuid: providerUuid?.length
                  ? providerUuid[0]
                  : undefined,
                type: row?.type,
              });
              ViewRecordModalDisclosure.open();
            }}
          >
            {row?.type
              ? ENTITY_TYPE_MAPPER[row.type as GetDataImportRecordsTypeEnum]
              : "-"}
          </Text>
        ),
      },
      {
        field: "title",
        headerName: "File Name",
        flex: 0.8,
        minWidth: 200,
      },
      {
        field: "userName",
        headerName: "User Name",
        flex: 1,
        minWidth: 200,
        renderCell: ({ value }) => (value && !!value?.trim() ? value : "-"),
      },
      {
        field: "totalRecords",
        headerName: "Total Records",
        flex: 0.6,
        minWidth: 150,
        renderCell: ({ row }) =>
          (row?.failedRecords ?? 0) + (row?.passedRecords ?? 0),
      },

      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: ({ row }) => getStatusByRecord(row),
      },
    ],
    ["totalRecords", "importInitiated"]
  );

  const [pagination, setPagination] = useState<DGT_Pagination>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [entityAndStatus, setEntityAndStatus] = useState<{
    entity?: GetDataImportRecordsTypeEnum;
    status: (typeof STATUS_OPTIONS)[number]["value"] | null;
  }>({ entity: undefined, status: "true" });
  const view = getViewAs();
  const realm = getStoredRealm();
  const { data: ImportedData } = useDataImportList({
    pagination,
    searchKey: "",
    type:
      (entityAndStatus.entity as any) === "all"
        ? undefined
        : entityAndStatus.entity,
    gridSorting,
    status:
      entityAndStatus?.status === "true"
        ? undefined
        : (entityAndStatus?.status ?? undefined),
    xTENANTID: view === "provider" ? (realm as string) : undefined,
  });

  function getStatusByRecord(row: DrugCatalogRowsType) {
    if (!row?.status) return "-";

    if (row.failedRecords !== 0 && row?.passedRecords !== 0)
      return (
        <Flex gap={"3px"} alignItems={"center"}>
          <StatusChip label="Partial" variant="warning" />
          <ErrorOutlineIcon fontSize="small" color="primary" />
        </Flex>
      );
    else if (row.failedRecords !== 0)
      return (
        <Box
          onClick={() => {
            downloadFailedRecords(row.uuid);
          }}
          sx={{ cursor: "pointer" }}
        >
          <StatusChip label="Fail" variant="error" />
        </Box>
      );

    return <StatusChip label="Pass" variant="success" />;
  }

  return (
    <PageLayout
      header={
        <Flex justifyContent={"space-between"}>
          <CustomTitle>Data Import</CustomTitle>
          <Flex
            gap={"16px"}
            width={"35rem"}
            justifyContent={"end"}
            alignItems={"center"}
          >
            <Box width={"12rem"}>
              <FilterSelect
                value={entityAndStatus.entity}
                onChange={(val) => {
                  setEntityAndStatus((prev) => ({
                    ...prev,
                    entity: val as any,
                  }));
                }}
                placeholder="Entity Type"
                options={[
                  {
                    label: "All",
                    value: "all",
                  },
                  ...EntityType,
                ]}
                clearable
              />
            </Box>
            <Box width={"12rem"}>
              <FilterSelect
                value={entityAndStatus.status}
                placeholder="Status"
                options={STATUS_OPTIONS}
                onChange={(val) => {
                  setEntityAndStatus((prev) => ({
                    ...prev,
                    status: val as any,
                  }));
                }}
              />
            </Box>

            {hasUpdateBulkUpload && (
              <StyledButtonV2
                label="Upload Data"
                onClick={UploadDataModalDisclosure.open}
                variant="outlined"
                startIcon={<FileUploadOutlinedIcon />}
              />
            )}
          </Flex>
        </Flex>
      }
    >
      {ViewRecordModalDisclosure.opened && (
        <ViewRecordDrawer
          importData={curDataImport}
          opened={ViewRecordModalDisclosure.opened}
          onClose={ViewRecordModalDisclosure.close}
        />
      )}
      {UploadDataModalDisclosure.opened && (
        <UploadData
          modalTitle="Upload"
          open={UploadDataModalDisclosure.opened}
          onClose={UploadDataModalDisclosure.close}
          hidePreviewStep={true}
        />
      )}
      <Box flex={1}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={ImportedData?.data?.content ?? []}
          columns={columns}
          rowCount={ImportedData?.data?.page?.totalElements ?? 10}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          sortModel={gridSorting}
        />
      </Box>
    </PageLayout>
  );
};

export default DataImport;
